// Returning-visitor session — "don't make me register twice".
//
// In the paywall_popup arms the LP owns the checkout hand-off: register → Upgate
// hosted page. A user who backs out of Upgate and returns to the lander lands on
// a FRESH page load, so every bit of React state (authToken, chosen plan, popup)
// is gone and the signup form would ask them to register again — with an email
// the backend now rejects ("User with this email already exists"). Remembering
// the registration here lets createAccount reopen the paywall popup wired to the
// token the user already has, so Continue goes straight back to checkout.
//
// STORAGE: sessionStorage, deliberately — same choice as the popup_first plan
// carry (csfw_paywall_selected_plan). It survives leaving for Upgate and coming
// back in the SAME tab (that's the whole scenario) and leaves no auth token
// sitting on disk afterwards. The trade-off is that a visitor returning in a NEW
// tab (re-clicked the ad, cross-browser OAuth completion) starts from scratch —
// there the token on the URL, not this store, is what carries state.
//
// TOKEN LIFETIME (why there's still a TTL): `authToken` is not a JWT, it's a Redis
// key on the backend — `external_registration:<token>` (auth.service.ts), written
// through RedisService.set, which defaults to `EX 3600`. It is also DELETED the
// moment the platform spends it (loginPaywall → redisService.del). So the stored
// token is worth at most an hour and can die at any point before that — a
// long-lived tab must not replay a token Redis has already dropped, and consumers
// MUST treat a 401 as "session gone" and fall back to the auth form (see
// createAccount's handleResumeCheckoutError) rather than trusting this store.
//
// The redirect (`paywall`) arm stores the same record WITHOUT a token: there the
// platform consumes the token on arrival, so nothing is left to spend — but the
// email still lets a returning visitor land on the login card instead of a signup
// form that can only fail. Same shape is re-saved (token dropped) when a resumed
// checkout 401s.
import { clearAuth } from './storage'

const SESSION_KEY = 'csfw_lp_session'

// Backend Redis TTL is 3600s; stay under it so a resumed checkout doesn't fire
// with a token that expired while the tab sat open.
export const LP_SESSION_TTL_MS = 55 * 60 * 1000

const writeSession = session => {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(session))
  } catch (e) {
    // ignore storage failures (private mode, quota)
  }
}

// session: { authToken?, userId?, email?, source: 'password'|'magic_link'|'google' }
export const saveLpSession = session => {
  if (typeof window === 'undefined' || !session?.source) return
  writeSession({ ...session, ts: Date.now() })
}

// Record the hand-off to the Upgate hosted page (runUpgateCheckout calls this
// right before navigating), so a return — browser Back, a cancel bounce, a
// back/forward-cache restore — knows this visitor was mid-purchase and puts the
// paywall popup back up instead of leaving them on the signup form.
// Keeps the original `ts`: the TTL tracks the TOKEN's life in Redis, so refreshing
// it here would let a dead token look alive.
export const markCheckoutStarted = () => {
  const current = readLpSession()
  if (!current) return
  writeSession({ ...current, checkoutAt: Date.now() })
}

// Null when absent, malformed, or past the TTL (expired entries are dropped).
export const readLpSession = () => {
  if (typeof window === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(SESSION_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed?.source || !parsed?.ts) return null
    if (Date.now() - parsed.ts > LP_SESSION_TTL_MS) {
      sessionStorage.removeItem(SESSION_KEY)
      return null
    }
    return parsed
  } catch (e) {
    return null
  }
}

// ---- OAuth-return one-shot guard --------------------------------------------
// The Google OAuth return handler is one-shot per auth_token: it fires the
// registration events and, in the popup_first arm, starts the Upgate checkout
// automatically. In-memory that guard is a useRef, which a page RELOAD wipes —
// and the return URL's params outlive it. Browser Back from Upgate restores the
// LP history entry, and the replaceState that stripped those params is not
// reliably what comes back, so the effect can run a SECOND time on the same
// token: duplicate registration events, and — the visible symptom — the user is
// shoved straight back into checkout instead of being allowed to change their
// plan.
const OAUTH_HANDLED_KEY = 'csfw_oauth_return_handled'

export const markOauthReturnHandled = authToken => {
  if (typeof window === 'undefined' || !authToken) return
  try {
    sessionStorage.setItem(OAUTH_HANDLED_KEY, authToken)
  } catch (e) {
    // ignore storage failures (private mode, quota)
  }
}

// True when THIS token's return was already processed in this tab. Compared by
// value, not a boolean flag: a second, genuinely new OAuth hop in the same tab
// mints a different token and must be handled normally.
export const wasOauthReturnHandled = authToken => {
  if (typeof window === 'undefined' || !authToken) return false
  try {
    return sessionStorage.getItem(OAUTH_HANDLED_KEY) === authToken
  } catch (e) {
    return false
  }
}

export const clearLpSession = () => {
  if (typeof window !== 'undefined') {
    try {
      sessionStorage.removeItem(SESSION_KEY)
      sessionStorage.removeItem(OAUTH_HANDLED_KEY)
    } catch (e) {
      // ignore
    }
  }
  // api.js's setAuth mirrors tokens into the legacy localStorage keys (magic-link
  // / OTP / login paths). A dead session should take those with it rather than
  // leave a spent token behind.
  clearAuth()
}
